import { fetchAssessmentIntakeForm } from "@/modules/third-party-risk/requests/intake-form-v2";
import { mapV2QuestionsToIntakeQuestions } from "@/modules/third-party-risk/pages/intake-review/utils/map-v2-to-intake-question";
import { addNotification } from "@/shared/states/notification";
import { useCallback, useEffect, useState } from "react";
import type { IntakeFormQuestion } from "../requests/vendor-intake-form-response";

interface UseIntakeFormQuestionsProps {
  assessmentId: string;
}

export const useIntakeFormQuestions = ({
  assessmentId,
}: UseIntakeFormQuestionsProps) => {
  const [questions, setQuestions] = useState<IntakeFormQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadQuestions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchAssessmentIntakeForm(assessmentId);
      const mapped = mapV2QuestionsToIntakeQuestions(data.questions);
      const visibleQuestions = mapped.filter((q) => !q.hidden_from_user);
      setQuestions(visibleQuestions);
    } catch (err) {
      console.error("Error loading questions:", err);
      setError(err instanceof Error ? err : new Error("Failed to load intake form questions"));
      addNotification({
        type: "error",
        message: "Failed to load intake form questions",
      });
    } finally {
      setIsLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    if (assessmentId) {
      void loadQuestions();
    } else {
      setIsLoading(false);
    }
  }, [assessmentId, loadQuestions]);

  const refetch = useCallback(() => {
    void loadQuestions();
  }, [loadQuestions]);

  return {
    questions,
    isLoading,
    error,
    refetch,
  };
};
